import { Button } from '@sw-sw/lib-ui';
import { kebabCase } from 'lodash';
import React, { PropsWithChildren, useContext, useState } from 'react';
import RolesContext from '../../../contexts/RolesContext';
import { ActionButtonsWithScrollFix } from '../../Shared/ActionButtons';
import InspectionReopenConfirmationModal from './InspectionReopenConfirmationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { InspectionContext } from '../../../contexts/InspectionContext';
import { ProjectContext } from '../../../contexts/ProjectContext';
import { QuestionFeature } from '@sw-sw/lib-inspection-templates';
import { FormContext } from '@sw-sw/lib-form';

/** Provides action buttons for any template (legacy or user-generated) */
export const AnyTemplateLayout: React.FC<
  PropsWithChildren<{
    templateName: string;
    onSave: () => Promise<void>;
    busy?: boolean;
    error?: string;
    isReadOnly?: boolean;
    isPublic?: boolean;
    onSaveAndContinue?: Function;
    isInspectionReadOnly?: boolean;
  }>
> = ({
  children,
  templateName,
  onSave,
  error,
  busy = false,
  isReadOnly = false,
  isPublic = false,
  isInspectionReadOnly = false,
  onSaveAndContinue
}) => {
    const permCheck = useContext(RolesContext).userHasPermission;
    const inspection = useContext(InspectionContext)
    const projectContext = useContext(ProjectContext)
    const formContext = useContext(FormContext);
    const [showReopenConfirmationModal, setShowReopenConfirmationModal] =
      useState<boolean>(false);
    const isEditing = inspection.isEditing;
    const setIsEditing = inspection.setIsEditing;

    const handleSave = async () => {
      await onSave();
      setIsEditing(false);
    };

    const handleSaveAndContinue = async () => {
      if (onSaveAndContinue) {
        await onSaveAndContinue();
      }
      setIsEditing(false);
    };

    const updateInspectionDateQuestions = () => {
      if (inspection.template && inspection.template.question_groups && inspection.isInspectionBackdatingDisabled) {
        const currentDate = new Date().toISOString();
        const updatedInspection: Record<string, string> = {};
        
        inspection.template.question_groups.forEach((group: any) => {
          group.questions.forEach((question: any) => {
            if (question.features && question.features.includes(QuestionFeature.isInspectionDate)) {
              updatedInspection[question.id] = currentDate;
              
              // For DynamicTemplateQuestions (user-generated templates), update FormContext
              if (formContext && formContext.set) {
                formContext.set(`question-${question.id}`, currentDate);
              }
            }
          });
        });
        
        // For LegacyTemplateQuestions, update the inspection object with new date values
        if (Object.keys(updatedInspection).length > 0) {
          inspection.updateInspection({
            ...inspection.inspection,
            ...updatedInspection
          });
        }
      }
    };

    const onclick = () => {
      setShowReopenConfirmationModal(true)
      inspection.setIsInspectionReadOnly(false)
    }

    return (
      <section
        className={`inspection-questions inspection-questions-${kebabCase(
          templateName,
        )}`}
      >
        {!isPublic && (isEditing || (!isReadOnly && !isInspectionReadOnly)) && (
          <ActionButtonsWithScrollFix
            primary='Save'
            onClick={handleSave}
            busy={busy}
            error={error}
            spacer={false}
            saveAndContinue={true}
            primaryDisabled={!projectContext.isDownloadedByLoggedInUser()}
            saveAndContinueDisabled={!projectContext.isDownloadedByLoggedInUser()}
            onSaveAndContinue={handleSaveAndContinue}
          />
        )}

        {!isPublic && !isEditing && isReadOnly && permCheck("all", "reopenInspection") && !isInspectionReadOnly && projectContext.isDownloadedByLoggedInUser() &&  (
          <div className="action-buttons">
            <Button primary onClick={() => onclick()}>
              <React.Fragment>
                <FontAwesomeIcon icon={faPencilAlt} /> Edit Inspection
              </React.Fragment>
            </Button>
          </div>
        )}
        {!isEditing && isInspectionReadOnly && !isPublic && permCheck("update", "Inspection") && projectContext.isDownloadedByLoggedInUser() && (
          <div className="action-buttons">
            <Button primary onClick={() => {
              setIsEditing(true);
              inspection.setIsInspectionReadOnly(false);
              inspection.reloadInspection();
              updateInspectionDateQuestions();
            }}>
              <React.Fragment>
                <FontAwesomeIcon icon={faPencilAlt} /> Edit Inspection
              </React.Fragment>
            </Button>
          </div>)
        }
        {children}
        {showReopenConfirmationModal && (
          <InspectionReopenConfirmationModal
            onClose={() => {
              setShowReopenConfirmationModal(false);
            }}
            onConfirm={() => {
              setIsEditing(true);
            }}
          />
        )}
      </section>
    );
  };
